/**
 * App.js — FadeFlow customer booking web app
 *
 * URL structure: fadeflow.app/book/:slug
 * The :slug identifies which barbershop the customer is booking with.
 * We look up the shop in Firestore (shops where slug == :slug),
 * then show the right shop's booking experience.
 */
import React, { useEffect, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useParams,
} from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';

import Login    from './pages/Login';
import Home     from './pages/Home';
import Book     from './pages/Book';
import Messages from './pages/Messages';
import Nav      from './components/Nav';

// ── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div style={{ color: 'var(--accent)', fontSize: 18 }}>Loading…</div>
    </div>
  );
}

// ── NotFound ─────────────────────────────────────────────────────────────────
function NotFound({ slug }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>✂️</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>Shop Not Found</div>
      <div style={{ fontSize: 15, color: 'var(--subtext)', lineHeight: 1.5 }}>
        We couldn't find a barbershop at <strong style={{ color: 'var(--accent)' }}>fadeflow.app/book/{slug}</strong>.
        <br /><br />
        Double-check the link you were given.
      </div>
    </div>
  );
}

// ── ShopRoot — resolves slug → shopId, handles auth ──────────────────────────
function ShopRoot() {
  const { slug } = useParams();

  const [user,  setUser]  = useState(undefined); // undefined = loading
  const [shop,  setShop]  = useState(undefined); // undefined = loading
  const [found, setFound] = useState(true);

  // Resolve slug → shop
  useEffect(() => {
    if (!slug) { setFound(false); setShop(null); return; }
    const q = query(collection(db, 'shops'), where('slug', '==', slug));
    getDocs(q).then(snap => {
      if (snap.empty) {
        setFound(false);
        setShop(null);
      } else {
        const d = snap.docs[0];
        setShop({ id: d.id, ...d.data() });
      }
    }).catch(() => { setFound(false); setShop(null); });
  }, [slug]);

  // Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return unsub;
  }, []);

  // Still loading
  if (shop === undefined || user === undefined) return <Spinner />;

  // Slug not found
  if (!found) return <NotFound slug={slug} />;

  // Not logged in
  if (!user) return <Login shop={shop} shopId={shop.id} slug={slug} />;

  // Logged in → show app
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', paddingBottom: 80 }}>
      <Routes>
        <Route index                  element={<Home     shop={shop} shopId={shop.id} slug={slug} />} />
        <Route path="book"            element={<Book     shop={shop} shopId={shop.id} slug={slug} />} />
        <Route path="messages"        element={<Messages shop={shop} shopId={shop.id} slug={slug} />} />
        <Route path="*"               element={<Navigate to={`/book/${slug}`} replace />} />
      </Routes>
      <Nav slug={slug} />
    </div>
  );
}

// ── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Main booking entry point */}
        <Route path="/book/:slug/*" element={<ShopRoot />} />

        {/* Redirect /book → generic not-found look */}
        <Route path="/book" element={<NotFound slug="" />} />

        {/* Landing / catch-all */}
        <Route path="*" element={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 64, marginBottom: 12 }}>✂️</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>FadeFlow</div>
            <div style={{ fontSize: 15, color: 'var(--subtext)' }}>The booking app for barbers</div>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}
